/**
 * Content Grid: responsive CSS grid with optional Swiper carousel below 1024px.
 */
import Swiper from 'swiper/bundle';

const BLOCK_SEL = '.acf-block--content-grid.is-carousel-on-mobile';
const GRID_SEL = '.content-grid__items';
const NAV_SEL = '.content-grid__nav';
const PAGINATION_SEL = '.content-grid__pagination';
const TABLET_AND_BELOW = '(max-width: 1023px)';

function isEditor() {
  return (
    document.body.classList.contains('block-editor-page')
    || document.body.classList.contains('wp-admin')
    || Boolean(document.querySelector('.block-editor-writing-flow'))
  );
}

function getDirectChildren(grid) {
  return [...grid.children].filter(
    (el) => !el.classList.contains('swiper-wrapper') && !el.classList.contains('block-list-appender'),
  );
}

function carouselConfig(block, slideCount, nav, pagination) {
  const columnsMobile = Math.max(1, Math.min(6, parseInt(block.dataset.columnsMobile || '1', 10)));
  const columnsTablet = Math.max(1, Math.min(6, parseInt(block.dataset.columnsTablet || '2', 10)));
  const gap = Math.max(0, parseInt(block.dataset.gap || '20', 10));
  const navigationType = block.dataset.navigationType || 'arrows';

  const config = {
    slidesPerView: columnsMobile,
    spaceBetween: gap,
    speed: 500,
    grabCursor: true,
    watchOverflow: true,
    loop: slideCount > columnsTablet,
    a11y: { enabled: true },
    breakpoints: {
      640: {
        slidesPerView: columnsTablet,
      },
    },
  };

  if (navigationType === 'arrows' && nav && slideCount > 1) {
    config.navigation = {
      nextEl: nav.querySelector('.content-grid__nav-btn--next'),
      prevEl: nav.querySelector('.content-grid__nav-btn--prev'),
    };
  }

  if (navigationType === 'dots' && pagination && slideCount > 1) {
    config.pagination = {
      el: pagination,
      clickable: true,
    };
  }

  return config;
}

function mountSwiperStructure(grid) {
  if (grid.dataset.swiperMounted === 'true') {
    return;
  }

  const items = getDirectChildren(grid);
  if (!items.length) {
    return;
  }

  const wrapper = document.createElement('div');
  wrapper.className = 'swiper-wrapper';

  items.forEach((item) => {
    item.classList.add('content-grid__item');
    const slide = document.createElement('div');
    slide.className = 'swiper-slide content-grid__slide';
    slide.appendChild(item);
    wrapper.appendChild(slide);
  });

  grid.classList.add('swiper');
  grid.appendChild(wrapper);
  grid.dataset.swiperMounted = 'true';
}

function unmountSwiperStructure(grid) {
  if (grid.dataset.swiperMounted !== 'true') {
    return;
  }

  const wrapper = grid.querySelector('.swiper-wrapper');
  if (wrapper) {
    wrapper.querySelectorAll('.swiper-slide').forEach((slide) => {
      const item = slide.querySelector('.content-grid__item') || slide.firstElementChild;
      if (item) {
        item.classList.remove('content-grid__item');
        grid.appendChild(item);
      }
    });
    wrapper.remove();
  }

  grid.classList.remove('swiper', 'swiper-initialized', 'swiper-horizontal', 'swiper-backface-hidden');
  delete grid.dataset.swiperMounted;
}

function initBlock(block) {
  if (isEditor()) {
    return;
  }

  const grid = block.querySelector(GRID_SEL);
  if (!grid) {
    return;
  }

  const nav = block.querySelector(NAV_SEL);
  const pagination = block.querySelector(PAGINATION_SEL);
  const tabletAndBelow = window.matchMedia(TABLET_AND_BELOW);
  let swiper = null;

  function enableCarousel() {
    if (swiper) {
      return;
    }

    mountSwiperStructure(grid);
    const slideCount = grid.querySelectorAll('.swiper-slide').length;
    if (slideCount === 0) {
      return;
    }

    swiper = new Swiper(grid, carouselConfig(block, slideCount, nav, pagination));
  }

  function disableCarousel() {
    if (swiper) {
      swiper.destroy(true, true);
      swiper = null;
    }
    unmountSwiperStructure(grid);
  }

  function onBreakpointChange(event) {
    if (event.matches) {
      enableCarousel();
    } else {
      disableCarousel();
    }
  }

  tabletAndBelow.addEventListener('change', onBreakpointChange);

  if (tabletAndBelow.matches) {
    enableCarousel();
  }
}

function initContentGrids() {
  document.querySelectorAll(BLOCK_SEL).forEach(initBlock);
}

function whenReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    queueMicrotask(fn);
  }
}

whenReady(initContentGrids);

if (typeof window.acf !== 'undefined') {
  window.acf.addAction('render_block_preview/type=content-grid', initContentGrids);
  window.acf.addAction('render_block_preview/type=acf/content-grid', initContentGrids);
}
